import React, { useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import CountBadge from '../../components/notifications/CountBadge';
import DashboardThemeToggle from '../../components/dashboard/DashboardThemeToggle';
import TestnetFaucetLink from '../../components/nav/TestnetFaucetLink';
import { useNotificationsOptional } from '../../context/NotificationContext';
import { adminNavigation, navigation } from '../../config/data';
import { JOBS_PORTAL_PATH } from '../../config/paths';
import { WalletAddressContext } from '../../constant/constant';

const SectionLabel = ({ children }) => (
    <p className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-500">
        {children}
    </p>
);

const navItemClass = (isActive) =>
    `group relative mt-1 flex items-center gap-3 rounded-lg px-3 py-2.5 text-[14px] font-medium transition-colors duration-200
                ${
                    isActive
                        ? 'bg-teal-50 text-teal-900 ring-1 ring-inset ring-teal-200 dark:bg-teal-950/40 dark:text-teal-100 dark:ring-teal-900/60'
                        : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-900/70 dark:hover:text-zinc-100'
                }`;

const SubNavLink = ({ child, badge, onNavigate }) => (
    <Link
        to={child.href}
        onClick={onNavigate}
        className={`flex items-center gap-2 rounded-lg px-3 py-2 text-[13px] transition-colors duration-200 motion-reduce:transition-none ${
            child.isActive
                ? 'bg-teal-50 text-teal-900 ring-1 ring-inset ring-teal-200 dark:bg-teal-950/40 dark:text-teal-100 dark:ring-teal-900/60'
                : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-900/70 dark:hover:text-zinc-100'
        } ${child.animateIn ? 'animate-fade-in-quick' : ''}`}
        style={child.style}
    >
        <span>{child.name}</span>
        <CountBadge value={badge} />
    </Link>
);

const NavItem = ({ item, isActive, badge, submenu }) => {
    const iconWrapClass = `inline-flex h-5 w-5 items-center justify-center text-[15px] transition-colors
                    ${
                        isActive
                            ? 'text-teal-800 dark:text-teal-300'
                            : 'text-zinc-400 group-hover:text-zinc-700 dark:group-hover:text-zinc-200'
                    }`;

    const inner = (
        <>
            {isActive && (
                <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-teal-600 dark:bg-teal-500" />
            )}
            <span className={iconWrapClass}>
                <i className={`${item.icon} ${item.iconClass || ''}`}></i>
            </span>
            <span>{item.name}</span>
            <CountBadge value={badge} />
        </>
    );

    return (
        <div>
            <Link to={item.href} className={navItemClass(isActive)}>
                {inner}
            </Link>
            {submenu}
        </div>
    );
};

const Navbar = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const activeCompanySection = queryParams.get('section') || 'setup';
    const activeCompanyPanel = queryParams.get('panel') || '';
    const isCompanyRoute = location.pathname.startsWith(JOBS_PORTAL_PATH);

    const companyJobChildren = [
        {
            id: 'company-profile',
            name: 'Company Profile',
            href: `${JOBS_PORTAL_PATH}?section=setup&panel=company`,
            isActive:
                isCompanyRoute &&
                activeCompanySection === 'setup' &&
                activeCompanyPanel === 'company',
        },
        {
            id: 'employees',
            name: 'Employees',
            href: `${JOBS_PORTAL_PATH}?section=setup&panel=employee`,
            isActive:
                isCompanyRoute &&
                activeCompanySection === 'setup' &&
                (activeCompanyPanel === 'employee' ||
                    activeCompanyPanel === 'add-employee'),
        },
        {
            id: 'tasks',
            name: 'Tasks',
            href: `${JOBS_PORTAL_PATH}?section=create`,
            isActive: isCompanyRoute && activeCompanySection === 'create',
        },
        {
            id: 'workspace',
            name: 'Workspace',
            href: `${JOBS_PORTAL_PATH}?section=workspace`,
            isActive: isCompanyRoute && activeCompanySection === 'workspace',
        },
    ];

    const { isAdmin } = useContext(WalletAddressContext);
    const notifications = useNotificationsOptional();
    const menuBadges = notifications?.menuBadges ?? {
        ongoing: 0,
        disputes: 0,
        tasks: 0,
        companyJob: 0,
        workspace: 0,
    };

    const companyJobSubmenu = (
        <div
            className={`ml-9 overflow-hidden transition-all duration-300 ease-out motion-reduce:transition-none ${
                isCompanyRoute
                    ? 'max-h-[280px] opacity-100 translate-y-0'
                    : 'max-h-0 opacity-0 -translate-y-1 pointer-events-none'
            }`}
        >
            <div className="mt-1 space-y-1 border-l border-zinc-200 py-0.5 pl-2 dark:border-zinc-800">
                {companyJobChildren.map((child, index) => {
                    let childBadge = 0;
                    if (child.id === 'tasks') childBadge = menuBadges.tasks;
                    if (child.id === 'workspace') childBadge = menuBadges.workspace;

                    return (
                        <SubNavLink
                            key={child.id}
                            child={{
                                ...child,
                                animateIn: isCompanyRoute,
                                style: {
                                    animationDelay:
                                        isCompanyRoute && index > 0
                                            ? `${index * 45}ms`
                                            : undefined,
                                },
                            }}
                            badge={childBadge}
                        />
                    );
                })}
            </div>
        </div>
    );

    const isItemActive = (item) =>
        location.pathname === item.href ||
        (item.href === '/dispute' &&
            location.pathname.startsWith('/dispute')) ||
        (item.href === JOBS_PORTAL_PATH &&
            location.pathname.startsWith(JOBS_PORTAL_PATH));

    return (
        <aside
            id="logo-sidebar"
            className="hidden lg:block fixed top-0 left-0 w-64 h-screen z-30"
            aria-label="Sidebar"
        >
            <div className="flex h-full flex-col border-r border-zinc-200 bg-white shadow-[2px_0_12px_rgba(0,0,0,0.04)] dark:border-zinc-800 dark:bg-zinc-950 dark:shadow-[6px_0_18px_rgba(0,0,0,0.5)]">
                <div className="mt-20 flex-1 space-y-6 overflow-y-auto px-3 pb-4">
                    {/* MAIN section */}
                    <div>
                        <SectionLabel>Main</SectionLabel>
                        <div className="space-y-0.5">
                            {navigation.map((item) => {
                                const isActive = isItemActive(item);
                                let badge = 0;
                                if (item.badgeKey === 'ongoing') {
                                    badge = menuBadges.ongoing;
                                } else if (item.badgeKey === 'disputes') {
                                    badge = menuBadges.disputes;
                                } else if (item.id === 'company-job') {
                                    badge = menuBadges.companyJob;
                                }
                                return (
                                    <NavItem
                                        key={item.id}
                                        item={item}
                                        isActive={isActive}
                                        badge={badge}
                                        submenu={
                                            item.href === JOBS_PORTAL_PATH
                                                ? companyJobSubmenu
                                                : null
                                        }
                                    />
                                );
                            })}
                        </div>
                    </div>

                    {/* ADMIN section (only when current wallet is contract admin) */}
                    {isAdmin && (
                        <div>
                            <SectionLabel>Admin</SectionLabel>
                            <div className="space-y-0.5">
                                {adminNavigation.map((item) => {
                                    const isActive = isItemActive(item);
                                    return (
                                        <NavItem
                                            key={item.id}
                                            item={item}
                                            isActive={isActive}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
                <div className="shrink-0 border-t border-zinc-200 px-3 py-4 dark:border-zinc-800">
                    <div className="mb-3 px-1">
                        <TestnetFaucetLink className="w-full justify-center py-2" />
                    </div>
                    <p className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-500 dark:text-zinc-500">
                        Appearance
                    </p>
                    <DashboardThemeToggle />
                </div>
            </div>
        </aside>
    );
};

export default Navbar;
